import Config from '../config';
import uuidv4 from 'uuid/v4'; 
import File from '../model/file';
import Task from './task';

import { read } from '../utils/file';
import { diffHash } from '../model/diff';
import { requestify } from '../utils/promisify';
import { flatify } from './refbooks/field.utils';
import { hashify } from '../utils/arrays';
import { unixts } from '../utils/date';





// uploading = Promise that resolves with server-file-name
export function attach(task, originalName, fileType, fileSemantics, uploading, binaryFile) {
    switch (fileType) {
        case 'photos': fileType = 'PHOTO'; break;
        case 'files': fileType = 'FILE'; break;
        case 'videos': fileType = 'VIDEO'; break;
        case 'sounds': fileType = 'SOUND'; break;
    }

    const attrs = {
        file_name: originalName,
        file_type: fileType,
        semantics: fileSemantics, 
        description: undefined,
        main_photo: null,
        sticker_id: undefined,
        guid: uuidv4(),
        uploading
    };

    if (binaryFile)
        attrs.reading = read( binaryFile ).then( result => attrs.reading = result );

    task.set( 'attachments', [...task.get( 'attachments' ), attrs] );

    return attrs;
}

export function save(form, propsToSkip) {
    const { attachments, id: taskId, ...taskAttrs } = form.model.toJSON();
    const fields = hashify( 
        flatify( form.options.fields.nonSystem() ), field => [field.id, field] );

    // hash of non-deleted files
    const filesToDelete = hashify( attachments
        .filter( ({ deleted }) => deleted === true || deleted === 't' )
        .map( att => att.guid || att.id ) );
    
    const [attrsToSend, filesDeletedAutomaticallyList] = taskId
        ? adjust( {
            ...diffHash( form.TaskForm.diff( { fields: form.options.fields } ) ),
            // may be omitted from diff-hash when only files deleted
            fields: form.model.get( 'fields' )
        } )
        : adjust( taskAttrs );
    const filesDeletedAutomatically = hashify( 
        filesDeletedAutomaticallyList.map( att => att.guid || att.id ) )

    const newAttachments = attachments
        // new file and not scheduled to delete
        .filter( att => !att.id && !filesToDelete[att.guid || att.id] );

    // wait till the end of all new attachment uploading
    const uploading = newAttachments
        // attachment.uploading contains uploading Promise
        .filter( att => att.uploading instanceof Promise )
        .map( att => att.uploading );

    const deletingAttachments = attachments
        // must be existing file and scheduled to delete
        // and NOT deleted automatically via custom-field-file deletion
        .filter( att => att.id && filesToDelete[att.id] )
        .filter( att => !filesDeletedAutomatically[att.id] )
        .map( att => File.delete( { id: taskId }, att ) );

    return requestify( Promise.all( [...uploading, ...deletingAttachments ] ) )
        .then( result => ({ 
            ...attrsToSend, 
            attachments: newAttachments
                // skip "reading" and "uploading" Promise
                .map( ({ reading, uploading, ...attrs }) =>
                    // the first N items of result are names of uploaded files;
                    // seek items in newAttachments with empty names and resolve one by one
                    ({ file: result.shift(), ...attrs }) )
        }) )
        .then( attrsToSend => taskId
            // update
            ? new Task( { id: taskId } ) .patch( attrsToSend )
            // create
            : new Task( { 
                ...attrsToSend,
                date: unixts( new Date() ),
                text: attrsToSend.text || '-',
                organization_id: attrsToSend.organization_id || Config.organization(),
            } ).save() );

    function adjust({ ...attrs }) {
        for (let prop in propsToSkip) if (attrs.hasOwnProperty( prop ))
            delete attrs[prop]

        const filesDeletedAutomatically = [];
        const { fields, ...others } = attrs;
        if (fields)
            others.fields = traverseInstance( fields, filesDeletedAutomatically );

        others.attachments = newAttachments;

        return [others, filesDeletedAutomatically];
    }

    function traverseValue(field, value, filesDeletedAutomatically) {
        if (!value || !field)
            return value;

        switch (field.format) {
            case 'file':
                filesDeletedAutomatically.push( 
                    ...value.filter( ({ guid, id }) => filesToDelete[guid || id] ) );
                return value.filter( ({ guid, id }) => !filesToDelete[guid || id] );
            case 'composite':   
                return value.map( val => traverseInstance( val, filesDeletedAutomatically ) );
            default:            
                return value;
        }
    }

    function traverseNode(node, filesDeletedAutomatically) {
        if (!node)
            return node;

        const { field_id, value } = node;
        return {
            field_id,
            value: traverseValue( fields[field_id], value, filesDeletedAutomatically )
        };
    }

    function traverseInstance(json, filesDeletedAutomatically) {
        return Object.keys( json )
            .map( key => [key, json[key]] )
            .map( ([key, value]) => [
                key,
                key !== 'guid' ? traverseNode( value, filesDeletedAutomatically ) : value] )
            .reduce( (json, [key, value]) => (json[key] = value, json), {} );
    }
}


// WEBPACK FOOTER //
// ./src/js/model/task.utils.js